import React, { useEffect, useState } from "react";
import BookingCards from "../../../component/common/BookingCards";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as LocationIcon } from "../../../assets/location.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit.svg";
import Button from "../../../component/common/Button";
import httpServices from "../../../services/httpServices";
import { useUser } from "../../../context/UserContext";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";

// Define Yup validation schema

const schema = Yup.object().shape({
  guestDetails: Yup.array().of(
    Yup.object().shape({
      fullName: Yup.string().required("Full name is required"),
      mobileNumber: Yup.string()
        .required("Mobile number is required")
        .matches(/^[0-9]+$/, "Mobile number must be numeric"),
      email: Yup.string()
        .required("Email is required")
        .email("Email is invalid"),
      gender: Yup.string()
        .required("Gender is required")
        .typeError("Gender must be a string"),
      age: Yup.number()
        .typeError("Age must be a number")
        .transform((value) => (value === "" ? undefined : value))
        .required("Age is required")
        .min(1, "Age must be greater than 0")
        .max(100, "Age must be less than 100"),
      ticketType: Yup.string().default("Economical"),
      seatNumber: Yup.string().default("AF2"),
      seatPrice: Yup.number()
        .default(30)
        .min(0, "Seat price must be greater than or equal to 0"),
    })
  ),
  selectedTickets: Yup.array().of(
    Yup.object().shape({
      ticketType: Yup.string()
        .oneOf(["Economical", "Silver", "Gold"])
        .required("Ticket type is required"),
      ticketPrice: Yup.number()
        .min(0, "Ticket price must be at least 0")
        .required("Ticket price is required"),
      quantity: Yup.number()
        .min(1, "Quantity must be at least 1")
        .required("Quantity is required"),
    })
  ),
});

const EventBookingDetails = () => {
  const { id } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const [combinedData, setCombinedData] = useState();
  const getEventDetails = async () => {
    try {
      const { response, error } = await httpServices.get(
        `/event/details/${id}`,
        {
          skip: !id,
        }
      );
      if (error) {
        throw error;
      }
      if (response.status === 200) {
        console.log("Response from getEventDetails", response.data);
        setCombinedData(response.data?.data);
      }
    } catch (error) {
      console.log("Error", error);
    }
  };
  useEffect(() => {
    getEventDetails();
  }, [id]);

  const data = [
    {
      title: "Economical",
      price: "10",
      count: 1,
      seatNumber: ["EC1"],
    },
    // {
    //   title: "Silver",
    //   price: "30",
    //   count: 2,
    //   seatNumber: ["SL1", "SL2"],
    // },
  ];

  const subtotal = data.reduce(
    (acc, item) => acc + Number(item.price) * item.count,
    0
  );
  const vat = subtotal * 0.02; // Assuming VAT is 2%
  const total = subtotal + vat;

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema), // Apply Yup validation schema
    defaultValues: {
      guestDetails: data.flatMap((item) =>
        Array.from({ length: item.count }).map((_, index) => ({
          ticketType: item.title || "",
          seatNumber: item.seatNumber[index] || "", // Assign seat number based on index
          seatPrice: item.price || 0,
        }))
      ),
      selectedTickets: data.flatMap((item) =>
        Array.from({ length: item.count }).map((_, index) => ({
          ticketType: item.title,
          ticketPrice: item.price,
          quantity: item.count || 1,
        }))
      ),
    },
  });

  const onError = (error) => {
    console.log("Error:", error);
  };

  const onSubmit = async (formData) => {
    console.log("Form Data:", formData);
    const toastId = toast.loading("Booking in progress...");
    try {
      const { response, error } = await httpServices.post(
        `/eventTicketDetailsOfUser/booking/create`,
        {
          ...formData,
          eventId: id,
          userId: user?.userId,
        }
      );
      if (error) {
        throw error;
      }
      if (response.status === 201) {
        console.log("Response from getBookingDetails", response.data?.data);
        toast.update(toastId, {
          render: "Booking successful",
          type: "success",
          isLoading: false,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.log("Error", error);
      toast.update(toastId, {
        render: "Error booking tickets",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
    navigate(`/events/${id}/booking-confirmation`);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <div className="my-4 p-[10px] sm:!p-[12px] w-full bg-white rounded-[10px] min-h-[600px] flex justify-between items-start flex-wrap xl:!flex-nowrap xl:!gap-0 gap-4">
        <div className="w-full xl:w-[69%] py-[10px] px-[10px] xs:px-[16px] bg-[#F5F6FA] rounded-[10px] min-h-[916px]">
          <h1 className="gradient-text font-medium text-base mb-[16px]">
            Attendees details
          </h1>

          {data.map((item, index) => {
            return Array.from({ length: item.count }).map((_, i) => (
              <BookingCards
                key={`${index}-${i}`}
                title={item.title}
                register={register}
                value={item.price}
                count={i + 1}
                control={control}
                errors={errors}
                cardIndex={index + i}
              />
            ));
          })}

          {/* Error message for ticketCount */}
          {errors.ticketCount && (
            <p className="text-red-500">{errors.ticketCount.message}</p>
          )}
        </div>

        {/* Card */}
        <div className="xl:w-[29%]">
          <div className="bg-[#F5F6FA] min-h-[648px] w-full  py-[10px] px-[10px] xs:px-[16px] rounded-[10px] ">
            <img
              src={`https://zv.shellcode.cloud/upload/${combinedData?.event?.eventMedia[0]}`}
              alt="img"
              className="w-full h-[200px] xs:h-[250px] sm:h-[350px] xl:h-[205px] object-cover rounded-[10px]"
            />
            <NavLink to={"/events/sdjfhdf"}>
              <h1 className="text-dark-blue font-medium text-lg mt-[10px] mb-[6px] ">
                {combinedData?.event?.eventName}
              </h1>
            </NavLink>
            <div className="flex gap-1 ">
              <LocationIcon />

              <span className="text-[12px] text-[#AFB1B9]">
                {combinedData?.venue?.venueAddress}
              </span>
            </div>

            <div className="mt-[16px] pt-[12px] border-t border-t-[#C1CAE1]">
              <div className="flex justify-between items-center">
                <h3 className="text-[#263238] text-xs">Selected tickets</h3>
                <div className="flex gap-[6px] items-center">
                  <EditIcon />
                  <span className="text-dark-blue text-xs font-medium">
                    Edit
                  </span>
                </div>
              </div>
              <div>
                {data.map((item) => (
                  <div
                    key={item.title}
                    className="mt-[20px] mb-[8px] flex items-center justify-between"
                  >
                    <h1 className="text-[#7020FF] font-medium text-lg">
                      {item.title} x {item.count}
                    </h1>
                    <h6 className="text-black text-sm">
                      AED {Number(item.price) * item.count}.00
                    </h6>
                  </div>
                ))}

                <div className="mb-[22px] relative">
                  <span className="h-[3px] bg-transparent bg-repeat-x bg-[length:20px_3px] bg-[linear-gradient(to_right,#BFC6D9_40%,rgba(255,255,255,0)_20%)] block"></span>
                  <span className="w-[30px] h-[30px] bg-white absolute -left-7 block rounded-full top-1/2 translate-y-[-50%]"></span>
                  <span className="w-[30px] h-[30px] bg-white absolute -right-6 block rounded-full top-1/2 translate-y-[-50%]"></span>
                </div>

                <div className="mt-[17px] pb-[10px] border-b border-b-[#C1CAE1]">
                  <h1 className="text-[#263238] text-xs">Booking Summary</h1>

                  <div className="mt-[20px] flex items-center justify-between">
                    <h1 className="text-black font-semibold text-lg">
                      {data.reduce((acc, item) => acc + item.count, 0)} Tickets
                    </h1>
                    <h6 className="text-black text-sm">AED {subtotal}.00</h6>
                  </div>
                  <div className="mt-[8px] flex items-center justify-between">
                    <h1 className="text-black text-lg">VAT charge</h1>
                    <h6 className="text-black text-sm">
                      AED {vat.toFixed(2)}{" "}
                    </h6>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-[16px]">
                  <h1 className="text-black">Sub Total</h1>
                  <h1 className="text-black">AED {total.toFixed(2)}</h1>
                </div>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            content="Checkout"
            classes="text-sm  bg-blue-color mb-0 w-full text-center items-center justify-center"
          />
        </div>
      </div>
    </form>
  );
};

export default EventBookingDetails;
